/**
 * AC-1479: `PayloadFieldSelector` - multi-select widget that lets the user
 * pick payload dot-paths to project as columns in `QueueItemsList`.
 *
 * ## Surfaces
 *
 * - **Currently-selected chip row**: Every entry in `value` renders as a
 *   removable chip ABOVE the discovery list. This is the only surface
 *   that can show paths NOT in the discovery sample (oversize-skipped
 *   items, low-frequency fields, custom paths typed in a previous
 *   session) - and the only surface that can remove them. Paths that
 *   are ALSO in discovery stay checked in the checkbox list below; both
 *   surfaces read from the same `selection` array so they stay in sync.
 *
 * - **Discovery list**: Each path returned by `useQueuePayloadFields(queueId)`
 *   is rendered as a checkbox row showing the path plus its sampled
 *   count/total ratio. Pre-checked entries come from the controlled `value`
 *   prop. Toggling a row emits `onChange(newValue)`.
 *
 * - **Discovery error disclosure**: When `fieldsQuery.isError` is true,
 *   an alert message replaces the discovery list so the user sees the
 *   real failure rather than the misleading "no fields discovered"
 *   empty-state copy. The chip row and custom-path input remain
 *   functional - operators can still add and remove paths manually.
 *
 * - **Custom path entry**: A text input lets operators type a path that did
 *   not appear in discovery (e.g. recently-added field, sample didn't pick
 *   it up, oversize-skipped). The entry is validated against the same
 *   regex as the backend before it is appended.
 *
 * - **UI 8-entry cap**: To keep the items list visually balanced, the UI
 *   refuses to add a 9th entry and surfaces a "Up to 8 fields" hint.
 *   (Server cap is 20 - the UI cap is purely a usability limit.)
 *
 * - **Save button**: Dispatches the current selection through
 *   `useUpdateQueue(queueId).mutate({ display_payload_fields })`. The
 *   mutation's `onSuccess` invalidates `queryKeys.queue(queueId)` which
 *   cascades to `queuePayloadFields(queueId)` and the items-list cache so
 *   the new projection takes effect everywhere.
 *
 * - **Skipped-oversize disclosure**: When the discovery response reports
 *   `skipped_oversize > 0`, a hint surfaces so operators know why a path
 *   they expected to see may be missing from the dropdown.
 *
 * ## Wiring (handled by AC-1480, NOT this ticket)
 *
 * AC-1480 imports this component into the queue settings panel. Until then
 * the component is exported but not mounted. Tests verify the component in
 * isolation; the orphan check intentionally fails this ticket and clears
 * in AC-1480.
 */

import React, { useState } from 'react';

import { useQueuePayloadFields } from '../../hooks/queries/useQueuePayloadFields';
import { useUpdateQueue } from '../../hooks/queries/useQueueMutations';

/**
 * Props for `PayloadFieldSelector`.
 *
 * Controlled component: `value` is the current selection, `onChange` is
 * called whenever the selection changes (toggle, custom-add, etc.). The
 * Save button uses `value` as the payload to submit.
 */
export interface PayloadFieldSelectorProps {
  /** Queue whose payload fields are being configured. */
  queueId: number;
  /** Currently-selected dot-paths. */
  value: string[];
  /** Called with the next selection on every change. */
  onChange: (next: string[]) => void;
  /** Optional className merged with the root wrapper. */
  className?: string;
  /**
   * When true, suppresses the component's internal Save button so the parent
   * can consolidate persistence into a single submit (AC-1482). The parent
   * is then responsible for sending `value` to the server.
   */
  hideSave?: boolean;
}

/** UI cap on selected payload-field columns. Server cap is 20. */
const UI_CAP = 8;

/**
 * Same regex the backend enforces on `display_payload_fields` entries.
 * Top-level identifier or dot-separated identifiers; identifiers must start
 * with a letter or underscore and contain only letters, digits, underscores.
 */
const PATH_REGEX = /^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)*$/;

const PayloadFieldSelector: React.FC<PayloadFieldSelectorProps> = ({
  queueId,
  value,
  onChange,
  className,
  hideSave = false,
}) => {
  const fieldsQuery = useQueuePayloadFields(queueId);
  const updateMutation = useUpdateQueue(queueId);

  const [customInput, setCustomInput] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [capError, setCapError] = useState<string | null>(null);

  const selection = value;

  const toggle = (path: string) => {
    if (selection.includes(path)) {
      onChange(selection.filter((p) => p !== path));
      setCapError(null);
      return;
    }
    if (selection.length >= UI_CAP) {
      setCapError(`Up to ${UI_CAP} fields can be selected.`);
      return;
    }
    onChange([...selection, path]);
    setCapError(null);
  };

  /**
   * Removes `path` from the current selection regardless of whether it
   * appears in the discovery sample. Backs the "Currently selected" chip
   * row so operators can clear paths that are not in the discovery list
   * (oversize-skipped items, low-frequency fields, custom paths typed in
   * a previous session). Also clears any stale cap/validation errors so
   * the user can immediately type a new path.
   */
  const removeSelection = (path: string) => {
    onChange(selection.filter((p) => p !== path));
    setCapError(null);
    setValidationError(null);
  };

  const tryAddCustom = () => {
    const candidate = customInput.trim();
    if (candidate === '') {
      setValidationError('Enter a field path.');
      return;
    }
    if (!PATH_REGEX.test(candidate)) {
      setValidationError(
        'Invalid path. Must start with a letter or underscore and contain only letters, digits, underscores, and dots.',
      );
      return;
    }
    if (selection.includes(candidate)) {
      setValidationError('That field is already selected.');
      return;
    }
    if (selection.length >= UI_CAP) {
      setCapError(`Up to ${UI_CAP} fields can be selected.`);
      return;
    }
    onChange([...selection, candidate]);
    setCustomInput('');
    setValidationError(null);
    setCapError(null);
  };

  const handleSave = () => {
    updateMutation.mutate({ display_payload_fields: selection });
  };

  const data = fieldsQuery.data;
  const fields = data?.fields ?? [];
  const skippedOversize = data?.skipped_oversize ?? 0;

  return (
    <div
      className={`bg-white dark:bg-cap-dark text-gray-900 dark:text-warm-white p-4 rounded-md ${className ?? ''}`}
    >
      <h3 className="text-sm font-medium mb-2 text-gray-900 dark:text-warm-white">
        Payload field columns
      </h3>

      {fieldsQuery.isLoading ? (
        <div
          role="progressbar"
          data-testid="payload-fields-loading"
          className="text-sm text-gray-500 dark:text-gray-400 py-2"
        >
          Loading payload fields...
        </div>
      ) : (
        <>
          {/*
            "Currently selected" chip row. Renders every entry in
            `selection` so operators can see and remove paths that are NOT
            in the discovery sample (legitimate cases: oversize-skipped
            items, low-frequency fields, custom paths typed before).
            Paths that ALSO appear in discovery stay checked in the
            checkbox list below - both surfaces stay in sync because
            both read from the same `selection` array.
          */}
          {selection.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                Currently selected
              </p>
              <ul className="flex flex-wrap gap-2">
                {selection.map((path) => (
                  <li key={path}>
                    <span
                      data-testid="selected-field-chip"
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-100 border border-primary-200 dark:border-primary-700"
                    >
                      <span className="font-mono">{path}</span>
                      <button
                        type="button"
                        onClick={() => removeSelection(path)}
                        aria-label={`Remove ${path}`}
                        data-testid="remove-selected-field"
                        className="ml-0.5 leading-none text-primary-700 dark:text-primary-200 hover:text-red-600 dark:hover:text-red-400 focus:outline-none focus:ring-1 focus:ring-primary-500 rounded"
                      >
                        &times;
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {fieldsQuery.isError ? (
            <p
              role="alert"
              className="text-sm text-red-600 dark:text-red-400 py-2"
            >
              Failed to load payload fields:{' '}
              {fieldsQuery.error?.message ?? 'Unknown error'}
            </p>
          ) : fields.length > 0 ? (
            <ul className="space-y-1 mb-3">
              {fields.map((f) => {
                const checked = selection.includes(f.path);
                const pct = Math.round(f.frequency * 100);
                return (
                  <li key={f.path}>
                    <label className="flex items-center text-sm cursor-pointer text-gray-700 dark:text-gray-200">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(f.path)}
                        aria-label={f.path}
                        className="mr-2 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="font-mono">{f.path}</span>
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                        ({f.count}/{data?.sample_size ?? 0}, {pct}%)
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              No payload fields discovered. Add one manually below.
            </p>
          )}

          {skippedOversize > 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">
              {skippedOversize} item{skippedOversize === 1 ? '' : 's'} skipped
              because their payload exceeds 64 KB.
            </p>
          )}

          <div className="flex items-end gap-2 mb-2">
            <div className="flex-1">
              <label
                htmlFor={`payload-field-custom-input-${queueId}`}
                className="block text-xs text-gray-600 dark:text-gray-400 mb-1"
              >
                Add custom field path
              </label>
              <input
                id={`payload-field-custom-input-${queueId}`}
                type="text"
                value={customInput}
                onChange={(e) => {
                  setCustomInput(e.target.value);
                  setValidationError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    tryAddCustom();
                  }
                }}
                placeholder="custom.path"
                aria-label="Add custom field path"
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-warm-white"
              />
            </div>
            <button
              type="button"
              onClick={tryAddCustom}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 text-gray-700 dark:text-gray-200"
            >
              Add
            </button>
          </div>

          {validationError && (
            <p
              role="alert"
              className="text-xs text-red-600 dark:text-red-400 mb-2"
            >
              {validationError}
            </p>
          )}

          {capError && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">
              {capError}
            </p>
          )}

          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {selection.length} / {UI_CAP} selected
            </span>
            {!hideSave && (
              <button
                type="button"
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="px-3 py-1 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-md disabled:opacity-50"
              >
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            )}
          </div>

          {!hideSave && updateMutation.isError && (
            <p
              role="alert"
              className="text-xs text-red-600 dark:text-red-400 mt-2"
            >
              Save failed: {updateMutation.error?.message ?? 'Unknown error'}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default PayloadFieldSelector;
